import { useCallback, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiClientError, apiClient } from '../api';
import { ErrorNotice, Loading, formString } from './Common';
import { Turnstile } from './Turnstile';

export function AuthScreen() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['bootstrap-status'],
    queryFn: apiClient.bootstrapStatus,
    staleTime: 5_000,
  });
  const [mode, setMode] = useState<'login' | 'bootstrap' | 'invite'>(() =>
    new URLSearchParams(location.search).has('invite') ? 'invite' : 'login',
  );
  const [turnstileToken, setTurnstileToken] = useState('');
  const onToken = useCallback((token: string) => setTurnstileToken(token), []);
  const login = useMutation({
    mutationFn: apiClient.login,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const bootstrap = useMutation({
    mutationFn: ({ input, secret }: { input: Record<string, unknown>; secret: string }) =>
      apiClient.bootstrap(input, secret),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const acceptInvite = useMutation({
    mutationFn: apiClient.acceptInvite,
    onSuccess: async () => {
      history.replaceState({}, '', '/');
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  if (status.isLoading) return <Loading label="Checking setup…" />;
  const bootstrapAvailable = status.data?.data.bootstrapAvailable ?? false;
  const showTurnstile =
    mode === 'bootstrap' ||
    (login.error instanceof ApiClientError && login.error.code === 'turnstile_required');

  const submitLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    login.mutate({
      identity: form.get('identity'),
      password: form.get('password'),
      ...(turnstileToken ? { turnstileToken } : {}),
    });
  };

  const submitBootstrap = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    bootstrap.mutate({
      secret: formString(form, 'bootstrapSecret'),
      input: {
        username: form.get('username'),
        email: form.get('email') || undefined,
        displayName: form.get('displayName'),
        password: form.get('password'),
        ...(turnstileToken ? { turnstileToken } : {}),
      },
    });
  };
  const submitInvite = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    acceptInvite.mutate({
      token: form.get('token'),
      username: form.get('username'),
      email: form.get('email') || undefined,
      displayName: form.get('displayName'),
      password: form.get('password'),
    });
  };

  return (
    <main className="auth-layout" id="main-content">
      <section className="auth-brand">
        <img src="/icon.svg" width="72" height="72" alt="" />
        <p className="eyebrow">Internal field operations</p>
        <h1>Campus Flyer Operations</h1>
        <p className="campaign-line">Affordable. Connected. Accountable.</p>
      </section>
      <section className="auth-card">
        <div className="auth-tabs" role="tablist" aria-label="Authentication">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            onClick={() => setMode('login')}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'invite'}
            onClick={() => setMode('invite')}
          >
            Accept invite
          </button>
          {bootstrapAvailable ? (
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'bootstrap'}
              onClick={() => setMode('bootstrap')}
            >
              First admin
            </button>
          ) : null}
        </div>
        {mode === 'login' ? (
          <form className="form-stack" onSubmit={submitLogin}>
            <div>
              <h2>Welcome back</h2>
              <p>Sign in with the account created by your campaign administrator.</p>
            </div>
            {login.error ? <ErrorNotice error={login.error} title="Could not sign in" /> : null}
            <label>
              Username or email
              <input name="identity" autoComplete="username" required autoFocus />
            </label>
            <label>
              Password
              <input name="password" type="password" autoComplete="current-password" required />
            </label>
            {showTurnstile && status.data ? (
              <Turnstile siteKey={status.data.data.turnstileSiteKey} onToken={onToken} />
            ) : null}
            <button className="button button-primary button-full" disabled={login.isPending}>
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : mode === 'invite' ? (
          <form className="form-stack" onSubmit={submitInvite}>
            <div>
              <h2>Accept your invitation</h2>
              <p>Use the one-time token shared by a campaign administrator.</p>
            </div>
            {acceptInvite.error ? (
              <ErrorNotice error={acceptInvite.error} title="Could not accept invitation" />
            ) : null}
            <label>
              Invitation token
              <input
                name="token"
                type="password"
                autoComplete="off"
                minLength={32}
                defaultValue={new URLSearchParams(location.search).get('invite') ?? ''}
                required
              />
            </label>
            <label>
              Display name
              <input name="displayName" autoComplete="name" required />
            </label>
            <label>
              Username
              <input name="username" autoComplete="username" minLength={3} required />
            </label>
            <label>
              Email <span className="optional">(if the invitation specifies one)</span>
              <input name="email" type="email" autoComplete="email" />
            </label>
            <label>
              Password
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
              />
              <small>At least 12 characters with a letter and a number.</small>
            </label>
            <button className="button button-primary button-full" disabled={acceptInvite.isPending}>
              {acceptInvite.isPending ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        ) : (
          <form className="form-stack" onSubmit={submitBootstrap}>
            <div>
              <h2>Create the first administrator</h2>
              <p>This one-time form needs the bootstrap secret configured in Cloudflare.</p>
            </div>
            {bootstrap.error ? (
              <ErrorNotice error={bootstrap.error} title="Could not create administrator" />
            ) : null}
            <label>
              Bootstrap secret
              <input
                name="bootstrapSecret"
                type="password"
                autoComplete="off"
                minLength={32}
                required
              />
            </label>
            <label>
              Display name
              <input name="displayName" autoComplete="name" required />
            </label>
            <label>
              Username
              <input name="username" autoComplete="username" minLength={3} required />
            </label>
            <label>
              Email <span className="optional">(optional)</span>
              <input name="email" type="email" autoComplete="email" />
            </label>
            <label>
              Password
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
              />
              <small>At least 12 characters with a letter and a number.</small>
            </label>
            {status.data ? (
              <Turnstile siteKey={status.data.data.turnstileSiteKey} onToken={onToken} />
            ) : null}
            <button className="button button-primary button-full" disabled={bootstrap.isPending}>
              {bootstrap.isPending ? 'Creating administrator…' : 'Create administrator'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
